import { useCallback, useState } from 'react';

import type { ProvidersSummary } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/utils';
import { RANKINGS_BY_LABELS } from 'src/modules/designStudio/helpers/productSelection/providerRanks';
import { ProductTypeLabel } from 'src/modules/designStudio/helpers/productSelection/types';

class ProductRanks {
  private rankMaps: Record<ProductTypeLabel, Map<string, number> | undefined>;

  constructor() {
    this.rankMaps = {
      [ProductTypeLabel.HORIZONTAL_SIDING]: undefined,
      [ProductTypeLabel.VERTICAL_SIDING]: undefined,
      [ProductTypeLabel.SHINGLED_SIDING]: undefined,
      [ProductTypeLabel.BRICK_SIDING]: undefined,
      [ProductTypeLabel.STONE_SIDING]: undefined,
      [ProductTypeLabel.CONCRETE_SIDING]: undefined,
      [ProductTypeLabel.STUCCO_SIDING]: undefined,

      [ProductTypeLabel.SHINGLE_ROOF]: undefined,
      [ProductTypeLabel.TILE_ROOF]: undefined,
      [ProductTypeLabel.METAL_ROOF]: undefined,

      [ProductTypeLabel.TRIM]: undefined,
    };
  }

  public getRankMap = (productType: ProductTypeLabel): Map<string, number> => {
    if (!this.rankMaps[productType]) {
      this.rankMaps[productType] = new Map<string, number>(
        RANKINGS_BY_LABELS[productType].map(obj => [obj.id, obj.rank]),
      );
    }

    return this.rankMaps[productType] as Map<string, number>;
  };
}

export const useProductRankings = () => {
  const [productRanks] = useState(new ProductRanks());

  const rankProviders = useCallback(
    (productType: ProductTypeLabel, ids: string[]) => {
      const map = productRanks.getRankMap(productType);
      ids.sort((a, b) => {
        const rankA = map.get(a) ?? 100;
        const rankB = map.get(b) ?? 100;

        return rankA - rankB;
      });
      return ids;
    },
    [productRanks],
  );

  const rankProviderSummaries = useCallback(
    (
      productType: ProductTypeLabel,
      summaries: ProvidersSummary[],
    ): ProvidersSummary[] => {
      const map = productRanks.getRankMap(productType);
      summaries.sort((a, b) => {
        const rankA = map.get(a.provider.uuid) ?? 100;
        const rankB = map.get(b.provider.uuid) ?? 100;

        return rankA - rankB;
      });
      return summaries;
    },
    [productRanks],
  );

  return {
    rankProviders,
    rankProviderSummaries,
  };
};
